import { useCallback, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { toast } from 'sonner';

export interface ExperienceVersionMeta {
    id: number;
    activationId: number;
    versionNumber: number;
    source: string;
    createdBy: string | null;
    createdAt: string;
}

export interface ExperienceVersionsResponse {
    currentVersion: number | null;
    versions: ExperienceVersionMeta[];
}

/**
 * Experience version history: list, fetch, restore, and export versions, plus
 * the shared "experience source" operations (apply template / import from
 * another activation). History is append-only server-side; restores create a
 * new version rather than rewriting history.
 */
export default function useExperienceVersions(activationId: number) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();

    const [versions, setVersions] = useState<ExperienceVersionMeta[]>([]);
    const [currentVersion, setCurrentVersion] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!orgId || !activationId) return;
        setIsLoading(true);
        try {
            const response = await authenticatedApi.get(
                `/${orgId}/experiences/${activationId}/versions`,
            );
            const data: ExperienceVersionsResponse = response.data;
            setVersions(data.versions ?? []);
            setCurrentVersion(data.currentVersion ?? null);
        } catch (error) {
            console.error('Error loading experience versions:', error);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId]);

    const getVersion = useCallback(
        async (versionNumber: number) => {
            const response = await authenticatedApi.get(
                `/${orgId}/experiences/${activationId}/versions/${versionNumber}`,
            );
            return response.data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, activationId],
    );

    const restoreVersion = useCallback(
        async (versionNumber: number) => {
            try {
                const response = await authenticatedApi.post(
                    `/${orgId}/experiences/${activationId}/versions/${versionNumber}/restore`,
                );
                toast.success(`Restored version ${versionNumber}`);
                return response.data;
            } catch (error) {
                console.error('Error restoring version:', error);
                toast.error('Failed to restore version');
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, activationId],
    );

    const exportVersion = useCallback(
        async (versionNumber: number) => {
            try {
                const version = await getVersion(versionNumber);
                const json = version.stringDefinition;
                const blob = new Blob([json], { type: 'application/json' });
                const href = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = href;
                link.download = `experience-${activationId}-v${versionNumber}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            } catch (error) {
                console.error('Error exporting version:', error);
                toast.error('Failed to export version');
            }
        },
        [getVersion, activationId],
    );

    const applyTemplate = useCallback(
        async (
            templateId: number,
            templateValues: { [key: string]: string },
        ) => {
            try {
                const response = await authenticatedApi.post(
                    `/${orgId}/experiences/${activationId}/apply-template`,
                    { templateId, templateValues },
                );
                toast.success('Template applied');
                return response.data;
            } catch (error) {
                console.error('Error applying template:', error);
                toast.error('Failed to apply template');
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, activationId],
    );

    const importFromActivation = useCallback(
        async (sourceActivationId: number) => {
            try {
                const response = await authenticatedApi.post(
                    `/${orgId}/experiences/${activationId}/import-from/${sourceActivationId}`,
                );
                toast.success('Experience imported');
                return response.data;
            } catch (error: any) {
                console.error('Error importing experience:', error);
                const message =
                    error?.response?.data?.message ??
                    'Failed to import experience';
                toast.error(message);
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orgId, activationId],
    );

    return {
        versions,
        currentVersion,
        isLoading,
        refresh,
        getVersion,
        restoreVersion,
        exportVersion,
        applyTemplate,
        importFromActivation,
    };
}
